import { Form } from "@unform/web";
import anime from "animejs";
import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";

import { HTTPErrorCallback } from "Helpers/Error";
import moment from "Helpers/MomentCompat";

import UseDebounce from "Hooks/UseDebounce";
import { UseGlobalLoading } from "Hooks/UseGlobalLoading";
import UseServices from "Hooks/UseServices";

import Button from "Components/Global/Buttons";
import Input from "Components/Global/Form/Input";
import GetIcon from "Components/Global/Icons";
import ImageLoader from "Components/Global/ImageLoader";

import * as S from "./styles";

import { useTheme } from "styled-components";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

const Productions = () => {
  const theme = useTheme();
  const { api } = UseServices();
  const { loading, setLoading } = UseGlobalLoading();

  const [search, setSearch] = useState(undefined);
  const debouncedSearchValue = UseDebounce(search, 300);

  const [page, setPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);

  const [productions, setProductions] = useState([]);
  const [activeProductions, setActiveProductions] = useState([]);
  const [selected, setSelected] = useState(null);

  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  const [parentalRatingIcons, setParentalRatingIcons] = useState({});
  const [accessibilityIcons, setAccessibilityIcons] = useState({});

  useEffect(() => {
    getProductions({});

    function importAll(r) {
      let images = {};
      r.keys().map((item) => {
        images[item.replace("./", "").replace(".png", "")] = r(item);
      });
      return images;
    }

    // eslint-disable-next-line no-undef
    const parental_images_folder = require.context(
      "Assets/Common/ParentalRating",
      false,
      /\.(png)$/,
    );

    // eslint-disable-next-line no-undef
    const accessiblity_folder = require.context(
      "Assets/Common/Accessiblity",
      false,
      /\.(png)$/,
    );

    const parental_icons = importAll(parental_images_folder);
    const accessibility_icons = importAll(accessiblity_folder);

    setParentalRatingIcons(parental_icons);
    setAccessibilityIcons(accessibility_icons);
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (search === undefined) return;
    getProductions({ search: debouncedSearchValue });
    //eslint-disable-next-line
  }, [debouncedSearchValue]);

  const getProductions = async ({ search, page }) => {
    setLoading(true);

    try {
      const response = await api.post(
        `/productions/list-active-productions?page=${page || 1}&page_size=9&get_total_rows=true`,
        {
          search,
        },
      );

      setProductions((prev) =>
        page
          ? [...prev, ...response.data.productions]
          : response.data.productions,
      );
      setTotalRows(response.data.total_rows);

      if (!page) setActiveProductions(response.data.active_productions);

      setLoading(false);

      if (page) setPage(page);
    } catch (error) {
      HTTPErrorCallback(error);
    }
  };

  const deselect = () => {
    anime({
      targets: ".background",
      opacity: 0,
      duration: 1300,
    });

    setTimeout(() => {
      setSelected(null);
    }, 300);
  };

  const is_in_active_period = moment().isBetween(
    moment(selected?.active_start_at || "0001-01-01"),
    moment(selected?.active_finish_at || "9999-12-12"),
  );

  const should_show_buy_ticket =
    selected?.details?.ticket_link && is_in_active_period;

  const detail_fields = {
    idealization: "IDEALIZAÇÃO",
    original: "OBRA ORIGINAL",
    argument: "ARGUMENTO",
    text: "TEXTO",
    dramaturgy: "DRAMATURGIA",
    adaptation: "ADAPTAÇÃO",
    enactment: "ENCENAÇÃO",
    direction: "DIREÇÃO",
    artistic_collaboration: "COLABORAÇÃO ARTÍSTICA",
    musical_direction: "DIREÇÃO MUSICAL",
    movement_direction: "DIREÇÃO DE MOVIMENTO",
    coreography: "COREOGRAFIA",
    audiovisual_direction: "DIREÇÃO DE AUDIOVISUAL",
    art_direction: "DIREÇÃO DE ARTE",
    cast: "ELENCO",
    music: "MÚSICOS",
    cenary: "CENOGRAFIA",
    costumes: "FIGURINO",
    lightining: "ILUMINAÇÃO",
    light_designer: "DESIGNER DE LUZ",
    sound_designer: "DESIGNER DE SOM",
    research: "PESQUISA",
    cast_production: "PRODUÇÃO DE ELENCO",
    production_direction: "DIREÇÃO DE PRODUÇÃO",
    associate_producer: "PRODUTOR ASSOCIADO",
    production_coordination: "COORDENAÇÃO DE PRODUÇÃO",
    realization: "REALIZAÇÃO",
  };

  const should_shorten_description = selected?.description.length > 700;

  return (
    <S.Wrapper>
      {selected ? (
        <S.Modal>
          <S.GetBack onClick={() => deselect()}>
            <GetIcon name="ChevronLeft" color={theme.colors.neutral.white} />
            <span>Voltar</span>
          </S.GetBack>

          <S.Header>
            <h3>{selected.title}</h3>
          </S.Header>

          <S.MainContent>
            <S.Images>
              <Swiper navigation={false} slidesPerView={1}>
                {selected.images.map((image, index) => (
                  <SwiperSlide key={index}>
                    <S.Image src={image} alt={selected.title} />
                  </SwiperSlide>
                ))}
              </Swiper>
            </S.Images>

            <S.Column>
              <S.HtmlDescription
                onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
                dangerouslySetInnerHTML={{
                  __html: isDescriptionOpen
                    ? `
                    <p>${selected.description}</p>
                    ${should_shorten_description ? `<span>Ver menos</span>` : ""}
                    `
                    : `
                    <p>${selected.description.substring(0, 700)} ${should_shorten_description ? "..." : ""}</p>
                    ${should_shorten_description ? `<span>Saiba mais</span>` : ""}
                  `,
                }}
              />
            </S.Column>
          </S.MainContent>

          <S.Details>
            <p>
              {selected?.start_at
                ? `Estreia ${Intl.DateTimeFormat("pt-BR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  }).format(new Date(selected?.start_at))}`
                : ""}

              {selected?.start_at && selected.details.premiere_location
                ? " - "
                : ""}

              {selected.details.premiere_location ? (
                <strong>{selected.details.premiere_location}</strong>
              ) : (
                ""
              )}
            </p>
            {should_show_buy_ticket ? (
              <Button
                variant="light"
                text="Comprar Ingresso"
                onClick={() => {
                  window.open(selected?.details?.ticket_link, "_blank");
                }}
              />
            ) : null}

            {Object.keys(detail_fields).map((field) =>
              selected.details[field] ? (
                <S.Info key={field}>
                  <h4>{detail_fields[field]}</h4>
                  <p>{selected.details[field]}</p>
                </S.Info>
              ) : null,
            )}
          </S.Details>
        </S.Modal>
      ) : (
        <>
          {videoModalOpen ? (
            <S.VideoModalBackground onClick={() => setVideoModalOpen(false)}>
              <S.VideoModal>
                <GetIcon
                  name="X"
                  color={theme.colors.neutral.white}
                  onClick={() => setVideoModalOpen(false)}
                />
                <iframe
                  src={videoModalOpen.details.vimeo_link}
                  allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                  frameBorder="0"
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                  allowFullScreen
                ></iframe>
              </S.VideoModal>
            </S.VideoModalBackground>
          ) : null}

          <Form>
            <Input
              name="search"
              label="Pesquisar: "
              placeholder="Buscar projeto"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              prefix={{
                icon: {
                  name: "Search",
                  position: "right",
                  color: theme.colors.neutral.cinza200,
                },
              }}
            />
          </Form>

          <S.ActiveText>EM CARTAZ</S.ActiveText>

          {!debouncedSearchValue ? (
            <Swiper modules={[Navigation]} slidesPerView={1} navigation={true}>
              {activeProductions.map((production, index) => {
                const details = production?.details;

                return (
                  <S.ActiveProduction
                    key={index}
                    onClick={() => {
                      if (!details?.vimeo_link) return setSelected(production);

                      setVideoModalOpen(production);
                    }}
                  >
                    <S.ActiveImage key={index} show={index === 0}>
                      <S.ActiveInfos>
                        <h3>{production.title}</h3>

                        <S.PremiereDetails>
                          <p>
                            {details?.location && <p>{details?.location}</p>}{" "}
                            {details?.state && details?.city ? (
                              <p>
                                ({details?.state}, {details?.city})
                              </p>
                            ) : null}
                          </p>
                          {/* {production?.date_start_at &&
                          production?.date_finish_at ? (
                            <p>
                              De{" "}
                              {moment(production?.date_start_at).format(
                                "DD/MM/YYYY",
                              )}{" "}
                              até{" "}
                              {moment(production?.date_finish_at).format(
                                "DD/MM/YYYY",
                              )}
                            </p>
                          ) : null}{" "} */}
                          {details?.season_dates ? (
                            <p>{details?.season_dates}</p>
                          ) : null}{" "}
                          <S.Row>
                            {details?.time && <span>{details?.time}</span>}{" "}
                            {details?.parental_rating ? (
                              <img
                                src={
                                  parentalRatingIcons[details?.parental_rating]
                                }
                                alt={details?.parental_rating}
                              />
                            ) : null}
                            {details?.accessibility?.length > 0 ? (
                              <S.Row>
                                {details?.accessibility.map((accessibility) => (
                                  <img
                                    key={accessibility}
                                    src={accessibilityIcons[accessibility]}
                                    alt={accessibility}
                                  />
                                ))}
                              </S.Row>
                            ) : null}
                          </S.Row>
                        </S.PremiereDetails>

                        {details?.ticket_link ? (
                          <Button
                            variant="light"
                            text="Comprar Ingresso"
                            width="100%"
                            onClick={() => {
                              window.open(details?.ticket_link, "_blank");
                            }}
                          />
                        ) : null}
                      </S.ActiveInfos>

                      <ImageLoader
                        src={production.images[0]}
                        alt={production.title}
                        height={200}
                      />
                    </S.ActiveImage>
                  </S.ActiveProduction>
                );
              })}
            </Swiper>
          ) : null}

          <S.SectionTitle>PRODUÇÕES</S.SectionTitle>

          <S.Productions>
            {loading ? (
              <>
                <Skeleton
                  height={250}
                  width={"100%"}
                  baseColor="#878787"
                  highlightColor="#636262"
                />

                <Skeleton
                  height={250}
                  width={"100%"}
                  baseColor="#878787"
                  highlightColor="#636262"
                />

                <Skeleton
                  height={250}
                  width={"100%"}
                  baseColor="#878787"
                  highlightColor="#636262"
                />
              </>
            ) : null}
            {productions.map((production, index) => (
              <S.Production key={index} onClick={() => setSelected(production)}>
                <ImageLoader
                  src={production.images[0]}
                  alt={production.title}
                  height={200}
                />

                <h3>{production.title}</h3>

                <p>
                  {production.details.original
                    ? production.details.original
                    : production.details.text
                      ? production.details.text
                      : production.details.dramaturgy}
                </p>
              </S.Production>
            ))}
          </S.Productions>

          {totalRows > productions.length ? (
            <S.Button>
              <Button
                variant="secondary"
                onClick={() => getProductions({ search, page: page + 1 })}
                width="200px"
                loading={loading}
                text="Carregar mais"
              />
            </S.Button>
          ) : null}
        </>
      )}
    </S.Wrapper>
  );
};

export default Productions;
